import * as pc from 'playcanvas'
import type { VoxelAvatarState } from '../avatarState'
import {
  WALK_DURATION_S,
  WALK_SPEED_MPS,
  deskModelPosture,
  kitchenTargetFor,
  lookYawOffset,
  phaseOffsetSeconds,
  walkPosition,
  walkYawDeg,
  type CharacterHandle,
  type StationPlacement,
} from '../characters'
import { standPlacement } from './layout'
import { worldRoot } from './worldRoot'

export interface LightweightFigureMaterials {
  body: pc.StandardMaterial
  head: pc.StandardMaterial
}

export function createLightweightFigureMaterials(
  bodyColor = new pc.Color(0.22, 0.29, 0.34),
  headColor = new pc.Color(0.68, 0.48, 0.34),
): LightweightFigureMaterials {
  const material = (color: pc.Color, gloss: number): pc.StandardMaterial => {
    const value = new pc.StandardMaterial()
    value.diffuse = color
    value.gloss = gloss
    value.update()
    return value
  }
  return {
    body: material(bodyColor, 0.18),
    head: material(headColor, 0.12),
  }
}

export interface LightweightFigure {
  root: pc.Entity
  model: pc.Entity
}

export const LIGHTWEIGHT_IDLE_PERIOD_S = 3.6
export const LIGHTWEIGHT_IDLE_LIFT_M = 0.012
export const LIGHTWEIGHT_WALK_LIFT_M = 0.018
export const LIGHTWEIGHT_LEG_CENTER_Y = 0.39
export const LIGHTWEIGHT_LEG_Y_SCALE = 0.36

/** Lowest point of the unit-height-2 capsule leg after its model offset. */
export function lightweightLegBottomY(modelOffset = 0): number {
  return modelOffset + LIGHTWEIGHT_LEG_CENTER_Y - LIGHTWEIGHT_LEG_Y_SCALE
}

/** Small deterministic whole-body motion so batched workload figures breathe rather than freeze. */
export function lightweightMotionOffset(
  elapsedSeconds: number,
  phaseSeconds: number,
  state: VoxelAvatarState,
  walking: boolean,
): number {
  if (state === 'slumped') return 0
  const phase = (2 * Math.PI * (elapsedSeconds + phaseSeconds)) / LIGHTWEIGHT_IDLE_PERIOD_S
  if (walking) return Math.abs(Math.sin(phase * 2)) * LIGHTWEIGHT_WALK_LIFT_M
  if (state === 'alarmed') return Math.abs(Math.sin(phase * 1.5)) * LIGHTWEIGHT_IDLE_LIFT_M
  return Math.sin(phase) * LIGHTWEIGHT_IDLE_LIFT_M
}

/**
 * A readable, articulated low-poly figure for identity-free workload actors.
 * Every part shares one of two materials, so dynamic batching retains the
 * original few-submit performance profile without reducing people to capsules.
 */
export function createLightweightFigure(
  parent: pc.Entity,
  batchGroupId: number | undefined,
  materials: LightweightFigureMaterials,
  name: string,
): LightweightFigure {
  const root = new pc.Entity(name)
  const model = new pc.Entity(`${name}-model`)
  root.addChild(model)
  parent.addChild(root)

  const part = (
    suffix: string,
    type: 'box' | 'capsule' | 'sphere',
    position: readonly [number, number, number],
    scale: readonly [number, number, number],
    material: pc.Material,
    rotation: readonly [number, number, number] = [0, 0, 0],
  ): void => {
    const entity = new pc.Entity(`${name}-${suffix}`)
    entity.addComponent('render', { type })
    if (entity.render) {
      entity.render.castShadows = false
      if (batchGroupId !== undefined) entity.render.batchGroupId = batchGroupId
      for (const mesh of entity.render.meshInstances) mesh.material = material
    }
    entity.setLocalPosition(position[0], position[1], position[2])
    entity.setLocalEulerAngles(rotation[0], rotation[1], rotation[2])
    entity.setLocalScale(scale[0], scale[1], scale[2])
    model.addChild(entity)
  }

  part('torso', 'box', [0, 1.18, 0], [0.48, 0.66, 0.28], materials.body)
  part('hips', 'box', [0, 0.78, 0], [0.4, 0.22, 0.26], materials.body)
  part('head', 'sphere', [0, 1.68, 0], [0.33, 0.34, 0.33], materials.head)
  part('arm-left', 'capsule', [-0.34, 1.12, 0], [0.13, 0.56, 0.13], materials.body, [0, 0, -8])
  part('arm-right', 'capsule', [0.34, 1.12, 0], [0.13, 0.56, 0.13], materials.body, [0, 0, 8])
  // PlayCanvas's capsule primitive is 2 units tall before scaling. A 0.64 Y
  // scale therefore reached 0.25 m below the root despite grounded shoes.
  // These 0.36 scales span y=0.03..0.75 and meet the hips without crossing
  // the finished floor.
  part('leg-left', 'capsule', [-0.15, LIGHTWEIGHT_LEG_CENTER_Y, 0], [0.15, LIGHTWEIGHT_LEG_Y_SCALE, 0.16], materials.body)
  part('leg-right', 'capsule', [0.15, LIGHTWEIGHT_LEG_CENTER_Y, 0], [0.15, LIGHTWEIGHT_LEG_Y_SCALE, 0.16], materials.body)
  part('shoe-left', 'box', [-0.15, 0.08, -0.07], [0.18, 0.12, 0.32], materials.body)
  part('shoe-right', 'box', [0.15, 0.08, -0.07], [0.18, 0.12, 0.32], materials.body)
  return { root, model }
}

/**
 * LOD actor for unbound workflow runs. Real roster agents keep their authored
 * skinned avatars; identity-free runs retain readable posture and completion
 * movement without consuming 10–13 skinned primitives apiece.
 */
export function spawnLightweightStationFigure(
  app: pc.Application,
  batchGroupId: number | undefined,
  materials: LightweightFigureMaterials,
  seed: string,
): CharacterHandle {
  const figure = createLightweightFigure(
    worldRoot(app),
    batchGroupId,
    materials,
    `run-figure-${seed}`,
  )
  let state: VoxelAvatarState = 'idle'
  let station: StationPlacement | null = null
  let baseYaw = 0
  let lookElapsed = 0
  let motionElapsed = 0
  let destroyed = false
  let walk: {
    start: [number, number, number]
    target: [number, number, number]
    elapsed: number
  } | null = null
  const lookPhase = phaseOffsetSeconds(seed, 7)
  const motionPhase = phaseOffsetSeconds(seed, LIGHTWEIGHT_IDLE_PERIOD_S)

  const applyModelPosture = (): void => {
    const posture = deskModelPosture(state, walk !== null)
    figure.model.setLocalPosition(
      0,
      posture.y + lightweightMotionOffset(motionElapsed, motionPhase, state, walk !== null),
      0,
    )
    figure.model.setLocalEulerAngles(
      state === 'slumped' ? 45 : posture.pitch,
      0,
      0,
    )
  }

  const applyPlacement = (): void => {
    if (!station || walk) return
    const placement = standPlacement(station.position, station.rotY)
    figure.root.setLocalPosition(placement.x, placement.y, placement.z)
    baseYaw = placement.yaw
    figure.root.setEulerAngles(0, baseYaw, 0)
    applyModelPosture()
  }

  const beginWalk = (): void => {
    if (!station || station.position[1] > 0.1) return
    const from = standPlacement(station.position, station.rotY)
    const start: [number, number, number] = [from.x, 0, from.z]
    const target = kitchenTargetFor(seed)
    walk = { start, target, elapsed: 0 }
    figure.root.setLocalPosition(...start)
    figure.root.setEulerAngles(0, walkYawDeg(start, target), 0)
    applyModelPosture()
  }

  const onUpdate = (dt: number): void => {
    motionElapsed += dt
    if (walk) {
      walk.elapsed = Math.min(WALK_DURATION_S, walk.elapsed + dt)
      figure.root.setLocalPosition(
        ...walkPosition(walk.start, walk.target, WALK_SPEED_MPS, walk.elapsed),
      )
    }
    if (state === 'waiting' && station) {
      lookElapsed += dt
      figure.root.setEulerAngles(
        0,
        baseYaw + lookYawOffset(lookElapsed, lookPhase),
        0,
      )
    }
    applyModelPosture()
  }
  app.on('update', onUpdate)

  return {
    entity: figure.root,
    setAnimState(next): void {
      if (next === state || state === 'slumped') return
      state = next
      lookElapsed = 0
      walk = null
      if (next === 'celebrating') beginWalk()
      applyPlacement()
      applyModelPosture()
    },
    setStation(next): void {
      station = next
      applyPlacement()
    },
    destroy(): void {
      if (destroyed) return
      destroyed = true
      app.off('update', onUpdate)
      figure.root.destroy()
    },
  }
}
